import { motion, AnimatePresence } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'

interface Props {
  room: Room
  playerId: string
  send: SendFn
}

export default function HostTransferBanner({ room, playerId, send }: Props) {
  const isHost = playerId === room.hostId
  const myRequestPending = room.hostRequest === playerId
  const mySeatRequestPending = room.seatTakeoverRequester === playerId

  // Host: a seat-takeover request is pending. Show it first — it's an
  // identity merge with bigger consequences than a host swap.
  if (isHost && room.seatTakeoverRequester && room.seatTakeoverTarget
    && room.players[room.seatTakeoverRequester] && room.players[room.seatTakeoverTarget]) {
    const requesterName = room.players[room.seatTakeoverRequester]?.name || '???'
    const targetName = room.players[room.seatTakeoverTarget]?.name || '???'
    return (
      <Banner>
        <span>
          <b>{requesterName}</b> wants to take over <b>{targetName}</b>'s seat.
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => send('approve_seat_takeover', {})}
            className="bg-sage hover:bg-sage/90 text-warmblack font-display font-bold px-4 py-1.5 rounded-lg text-sm"
          >
            Approve
          </button>
          <button
            onClick={() => send('decline_seat_takeover', {})}
            className="bg-warmblack/10 hover:bg-warmblack/15 text-warmblack font-display font-bold px-4 py-1.5 rounded-lg text-sm"
          >
            Decline
          </button>
        </div>
      </Banner>
    )
  }

  // Requester side: faint hint that the takeover request is pending.
  if (mySeatRequestPending) {
    return (
      <Banner subtle>
        <span className="text-warmblack/70">Waiting for host approval to take over the seat…</span>
        <button
          onClick={() => send('cancel_seat_takeover', {})}
          className="text-warmblack/60 hover:text-coral underline underline-offset-2 text-xs"
        >
          Cancel
        </button>
      </Banner>
    )
  }

  // Host: someone wants to take over.
  if (isHost && room.hostRequest && room.players[room.hostRequest]) {
    const requesterName = room.players[room.hostRequest]?.name || '???'
    return (
      <Banner>
        <span>
          <b>{requesterName}</b> wants to be host.
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => send('approve_host_request', {})}
            className="bg-sage hover:bg-sage/90 text-warmblack font-display font-bold px-4 py-1.5 rounded-lg text-sm"
          >
            Approve
          </button>
          <button
            onClick={() => send('decline_host_request', {})}
            className="bg-warmblack/10 hover:bg-warmblack/15 text-warmblack font-display font-bold px-4 py-1.5 rounded-lg text-sm"
          >
            Decline
          </button>
        </div>
      </Banner>
    )
  }

  // Non-host requester: faint hint that the request is pending.
  if (!isHost && myRequestPending) {
    return (
      <Banner subtle>
        <span className="text-warmblack/70">Host request pending…</span>
        <button
          onClick={() => send('cancel_host_request', {})}
          className="text-warmblack/60 hover:text-coral underline underline-offset-2 text-xs"
        >
          Cancel
        </button>
      </Banner>
    )
  }

  return null
}

function Banner({ children, subtle = false }: { children: React.ReactNode; subtle?: boolean }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className={`fixed top-3 left-3 right-16 z-30 rounded-xl shadow-md px-4 py-2.5 flex items-center justify-between gap-3 font-body text-sm ${
          subtle ? 'bg-cream border border-warmblack/15 text-warmblack/70' : 'bg-white border border-coral/30 text-warmblack'
        }`}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}
